import { useState, useEffect } from 'react';
import { TONE_PROFILES } from './PresentationBannerConstants';

// ── WorkflowTimeline ──────────────────────────────────────────────────────────
interface WorkflowTimelineProps { generating: boolean; done: boolean; }

export function WorkflowTimeline({ generating, done }: WorkflowTimelineProps) {
  const steps = [
    { id: 'generate', icon: 'ri-sparkling-2-line', label: 'Generate', gradient: 'from-rose-500 to-pink-600' },
    { id: 'style',    icon: 'ri-palette-line',     label: 'Style',    gradient: 'from-violet-500 to-purple-600' },
    { id: 'polish',   icon: 'ri-slideshow-2-line',  label: 'Polish',   gradient: 'from-sky-500 to-blue-600' },
    { id: 'share',    icon: 'ri-share-circle-line', label: 'Share',    gradient: 'from-emerald-500 to-teal-600' },
  ];

  const getState = (id: string) => {
    if (id === 'generate') return done ? 'done' : 'active';
    if (id === 'style')    return done ? 'next' : 'pending';
    return 'pending';
  };

  return (
    <div className="px-6 pt-5 pb-4 border-b border-white/8">
      <div className="flex items-center justify-between">
        {steps.map((s, idx) => {
          const state = getState(s.id);
          return (
            <div key={s.id} className="flex items-center flex-1">
              <div className="flex flex-col items-center gap-1.5 relative">
                <div className={`w-9 h-9 rounded-xl flex items-center justify-center transition-all duration-500 ${state === 'done' ? `bg-gradient-to-br ${s.gradient} scale-105` : state === 'active' ? `bg-gradient-to-br ${s.gradient} ring-2 ring-white/25 ring-offset-1 ring-offset-transparent` : state === 'next' ? `bg-gradient-to-br ${s.gradient} opacity-70` : 'bg-white/8 border border-white/12'}`}>
                  {state === 'done' ? <i className="ri-check-line text-white text-sm"></i>
                    : (state === 'active' && generating) ? <i className={`${s.icon} text-white text-sm animate-pulse`}></i>
                    : <i className={`${s.icon} text-sm ${state === 'pending' ? 'text-slate-600' : 'text-white'}`}></i>}
                </div>
                <span className={`text-xs font-bold whitespace-nowrap transition-colors duration-300 ${state === 'done' || state === 'active' ? 'text-white' : state === 'next' ? 'text-slate-400' : 'text-slate-600'}`}>{s.label}</span>
                <span className={`text-xs whitespace-nowrap ${state === 'done' ? 'text-emerald-400 font-semibold' : state === 'active' && generating ? 'text-rose-300 font-semibold' : state === 'active' ? 'text-slate-400' : state === 'next' ? 'text-violet-400 font-semibold' : 'text-slate-700'}`}>
                  {state === 'done' ? '✓ Done' : state === 'active' && generating ? 'Building…' : state === 'active' ? 'Current' : state === 'next' ? '↑ Up next' : 'Pending'}
                </span>
                {state === 'active' && !done && <div className="absolute -top-1 -right-1 w-2.5 h-2.5 rounded-full bg-rose-400 animate-pulse"></div>}
                {state === 'next'   && <div className="absolute -top-1 -right-1 w-2.5 h-2.5 rounded-full bg-violet-400 animate-pulse"></div>}
              </div>
              {idx < steps.length - 1 && (
                <div className="flex-1 mx-2 mb-5">
                  <div className="h-px relative overflow-hidden rounded-full bg-white/10">
                    <div className="absolute inset-y-0 left-0 bg-gradient-to-r from-rose-500/70 to-violet-500/40 rounded-full transition-all duration-700"
                      style={{ width: state === 'done' ? '100%' : (state === 'active' && generating) ? '50%' : state === 'active' ? '20%' : '0%' }} />
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── DeckSummaryPanel ──────────────────────────────────────────────────────────
interface DeckSummaryProps {
  topic: string; slideCount: number; tone: string; visible: boolean; onClose: () => void;
}

export function DeckSummaryPanel({ topic, slideCount, tone, visible, onClose }: DeckSummaryProps) {
  const [mounted, setMounted]           = useState(false);
  const [animIn, setAnimIn]             = useState(false);
  const [barsAnimated, setBarsAnimated] = useState(false);
  const [copied, setCopied]             = useState(false);

  const profile          = TONE_PROFILES[tone] ?? TONE_PROFILES['Professional'];
  const estimatedMinutes = Math.max(1, Math.ceil(slideCount * 1.4));
  const wordCount        = Math.round(slideCount * 65);

  useEffect(() => {
    if (visible) {
      setMounted(true);
      const t1 = setTimeout(() => setAnimIn(true), 30);
      const t2 = setTimeout(() => setBarsAnimated(true), 450);
      return () => { clearTimeout(t1); clearTimeout(t2); };
    } else {
      setAnimIn(false);
      setBarsAnimated(false);
      const t = setTimeout(() => setMounted(false), 400);
      return () => clearTimeout(t);
    }
  }, [visible]);

  const handleShare = async () => {
    const lines = [
      '📊 Deck Summary — openclaw.ai', '',
      `Topic: ${topic || 'Untitled Presentation'}`,
      `Slides: ${slideCount}`, `Tone: ${tone} · ${profile.mood}`,
      `Est. Read: ${estimatedMinutes} min (~${wordCount} words)`, '',
      `Audience: ${profile.audience.join(', ')}`, '',
      `Traits: ${profile.traits.map(t => `${t.label} ${t.pct}%`).join(' · ')}`, '',
      'Created with openclaw.ai · https://openclaw.ai',
    ];
    try {
      await navigator.clipboard.writeText(lines.join('\n'));
      setCopied(true); setTimeout(() => setCopied(false), 2500);
    } catch { /* noop */ }
  };

  if (!mounted) return null;

  return (
    <div className={`fixed right-0 top-1/2 -translate-y-1/2 z-50 w-80 transition-all duration-400 ease-out ${animIn ? 'translate-x-0 opacity-100' : 'translate-x-full opacity-0'}`} style={{ transitionTimingFunction: 'cubic-bezier(0.22,1,0.36,1)' }}>
      <div className="absolute -inset-0.5 rounded-l-3xl opacity-40 blur-sm" style={{ background: `linear-gradient(135deg, ${profile.accentColor}60, transparent)` }} />
      <div className="relative rounded-l-3xl overflow-hidden border border-white/15 bg-slate-900/95 backdrop-blur-xl shadow-2xl">
        {/* Gradient header */}
        <div className={`bg-gradient-to-br ${profile.bg} border-b border-white/10 px-5 py-4`}>
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-center gap-2.5">
              <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: profile.accentColor + '25', border: `1px solid ${profile.accentColor}40` }}>
                <i className={`${profile.moodIcon} text-base`} style={{ color: profile.accentColor }}></i>
              </div>
              <div>
                <p className="text-xs font-extrabold text-white uppercase tracking-wider">Deck Summary</p>
                <p className="text-xs mt-0.5" style={{ color: profile.accentColor }}>AI Analysis Complete</p>
              </div>
            </div>
            <button onClick={onClose} className="w-7 h-7 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center cursor-pointer transition-colors flex-shrink-0 mt-0.5">
              <i className="ri-close-line text-slate-400 text-sm"></i>
            </button>
          </div>
          {topic && (
            <div className="mt-3 px-3 py-2 bg-black/20 rounded-xl">
              <p className="text-xs text-slate-400 mb-0.5">Topic</p>
              <p className="text-sm font-bold text-white leading-snug line-clamp-2">{topic}</p>
            </div>
          )}
        </div>

        <div className="px-5 py-4 space-y-5">
          {/* Quick stats */}
          <div className="grid grid-cols-3 gap-2">
            {[{ icon: 'ri-time-line', val: `${estimatedMinutes}m`, label: 'Read Time' }, { icon: 'ri-layout-grid-line', val: `${slideCount}`, label: 'Slides' }, { icon: 'ri-text-wrap', val: `~${wordCount}`, label: 'Words' }].map(stat => (
              <div key={stat.label} className="flex flex-col items-center gap-1 py-2.5 rounded-xl border border-white/8" style={{ background: profile.accentColor + '10' }}>
                <i className={`${stat.icon} text-sm`} style={{ color: profile.accentColor }}></i>
                <span className="text-sm font-extrabold text-white">{stat.val}</span>
                <span className="text-xs text-slate-500">{stat.label}</span>
              </div>
            ))}
          </div>

          {/* Tone mood */}
          <div>
            <p className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">Tone Mood</p>
            <div className="flex items-center gap-2.5 px-3 py-2.5 rounded-xl border border-white/10 bg-white/5">
              <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: profile.accentColor + '20' }}>
                <i className={`${profile.moodIcon} text-sm`} style={{ color: profile.accentColor }}></i>
              </div>
              <div>
                <p className="text-xs font-bold text-white">{tone}</p>
                <p className="text-xs text-slate-500">{profile.mood}</p>
              </div>
            </div>
          </div>

          {/* Tone analysis bars */}
          <div>
            <p className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-3">Tone Analysis</p>
            <div className="space-y-2.5">
              {profile.traits.map((trait, i) => (
                <div key={trait.label}>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs font-semibold text-slate-300">{trait.label}</span>
                    <span className="text-xs font-bold" style={{ color: trait.color }}>{trait.pct}%</span>
                  </div>
                  <div className="h-1.5 bg-white/8 rounded-full overflow-hidden">
                    <div className="h-full rounded-full transition-all duration-700" style={{ width: barsAnimated ? `${trait.pct}%` : '0%', backgroundColor: trait.color, transitionDelay: `${i * 120}ms` }} />
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Audience */}
          <div>
            <p className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-2.5">Suggested Audience</p>
            <div className="space-y-1.5">
              {profile.audience.map((aud, i) => (
                <div key={aud} className={`flex items-center gap-2.5 px-3 py-2 rounded-xl border border-white/8 transition-all duration-300 ${barsAnimated ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-4'}`} style={{ transitionDelay: `${300 + i * 80}ms`, background: profile.accentColor + '08' }}>
                  <div className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 text-xs font-bold" style={{ backgroundColor: profile.accentColor + '25', color: profile.accentColor }}>{i + 1}</div>
                  <span className="text-xs font-semibold text-slate-300">{aud}</span>
                </div>
              ))}
            </div>
          </div>

          <button onClick={handleShare} className={`w-full flex items-center justify-center gap-2 py-2.5 rounded-xl text-xs font-bold transition-all cursor-pointer whitespace-nowrap border ${copied ? 'bg-emerald-500/15 border-emerald-500/40 text-emerald-300' : 'bg-white/8 border-white/15 text-slate-300 hover:bg-white/14 hover:text-white'}`}>
            <i className={`text-xs ${copied ? 'ri-check-double-line' : 'ri-share-line'}`}></i>
            {copied ? 'Copied to Clipboard!' : 'Share Deck Summary'}
          </button>
          <button className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl text-white text-xs font-bold transition-all cursor-pointer hover:opacity-90 whitespace-nowrap" style={{ background: `linear-gradient(135deg, ${profile.accentColor}, ${profile.accentColor}bb)` }}>
            <i className="ri-edit-2-line"></i>Open in Editor
          </button>
        </div>
      </div>
    </div>
  );
}
